import SparkleIcon from "@ch/assets/icons/pack/Sparkle.tsx";
import Button from "@ch/components/Buttons/Button.tsx";
import type {
  ProgramPlansWithRelations,
  ProgramsWithRelations,
} from "@ch/generated";
import { isObjEmpty } from "@ch/helpers/utils";
import useFirebaseAnalytics from "@ch/helpers/utils/firebase/hooks/useFirebaseAnalytics.ts";
import { FirebaseEventEnum } from "@ch/helpers/utils/firebase/types.ts";
import { formatCurrency, sortByOrder } from "@ch/helpers/utils/helpers.ts";
import PriceCards from "@ch/pages/SubscriptionsPage/components/PriceCards.tsx";
import PlanBenefitPage from "@ch/pages/SubscriptionsPage/tabs/MyPlan/PlanBenefitPage.tsx";
import { useState } from "react";

interface IProps {
  program?: ProgramsWithRelations;
  planId?: string;
  hasExpired?: boolean | undefined;
}

function ProgramDetails({ program, planId, hasExpired }: IProps) {
  const { LogEvent } = useFirebaseAnalytics();
  const [selectedPlan, setSelectedPlan] = useState<ProgramPlansWithRelations>(
    {} as ProgramPlansWithRelations,
  );
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [planBenefits, setPlanBenefits] = useState<
    (string | undefined)[] | undefined
  >([]);

  return (
    <div className="">
      {!planId?.length ? (
        <h1 className="pb-6 md:pb-8">{program?.title}</h1>
      ) : (
        <div className="h-0" />
      )}
      {showPlanDetails ? (
        <PlanBenefitPage
          activeSubscription={!!planId?.length}
          benefits={planBenefits}
          hasExpired={hasExpired}
          hidePlanDetails={() => setShowPlanDetails(false)}
          planInfo={selectedPlan}
        />
      ) : (
        <>
          <div className="flex max-w-fit gap-3 rounded-2xl bg-orange-300 px-6 py-4">
            <span className="text-orange">
              <SparkleIcon />
            </span>
            <p className="text-xs">
              Select the plan you wish to subscribe to or continue with your
              current plan.
            </p>
          </div>
          <div className="my-8 flex flex-col gap-6 p-6 md:grid md:grid-cols-2 lg:grid-cols-3">
            {sortByOrder(program?.plans).map((elem) => {
              const benefits = elem?.plansBenefit
                ?.filter(
                  (item) => item.type === "checkmark" && item.value === "true",
                )
                .map((item) => item.benefit?.title);

              const text = benefits?.slice(0, 3)?.join(", ") ?? "";
              return (
                <PriceCards
                  checked={selectedPlan?.id === elem.id}
                  isCurrentPlan={planId === elem.id}
                  key={elem.id}
                  onChange={() => {
                    setSelectedPlan(elem);
                    setPlanBenefits(benefits);
                    LogEvent(FirebaseEventEnum.SELECT_PROGRAM_PLAN, {
                      selectedPlanId: elem.id ?? "",
                    });
                  }}
                  planDetails={text}
                  planName={elem.title}
                  price={
                    elem.basePrice === 0
                      ? "Free"
                      : `₦${formatCurrency(elem?.basePrice)} 
                      ${elem?.duration ? `/${elem?.duration}` : ""}`
                  }
                />
              );
            })}
          </div>
          <div className="text-right">
            <Button
              className="text-sm"
              disabled={isObjEmpty(selectedPlan)}
              onClick={() => setShowPlanDetails(true)}
            >
              {planId?.length ? "View Benefits" : "Continue"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default ProgramDetails;
